// Simple gizmo renderer for transform controls

const gizmoVertexShader = `#version 300 es
precision highp float;

uniform mat4 projection;
uniform mat4 view;
uniform mat4 model;
uniform vec3 gizmoPosition;
uniform float scale;

in vec3 position;
in vec3 color;

out vec3 vColor;

void main() {
    vec3 scaledPos = position * scale;
    vec4 worldPos = model * vec4(scaledPos + gizmoPosition, 1.0);
    gl_Position = projection * view * worldPos;
    vColor = color;
}`;

const gizmoFragmentShader = `#version 300 es
precision highp float;

in vec3 vColor;
out vec4 fragColor;

void main() {
    fragColor = vec4(vColor, 1.0);
}`;

export class GizmoRenderer {
    constructor(gl) {
        this.gl = gl;
        this.program = null;
        this.vao = null;
        this.mode = 'translate'; // 'translate', 'rotate', 'scale'
        this.selectedAxis = null;
        this.gizmoScale = 0.1;
        this.position = [0, 0, 0];
        
        this.init();
    }

    init() {
        const gl = this.gl;
        
        // Create shader program
        const vertShader = this.createShader(gl.VERTEX_SHADER, gizmoVertexShader);
        const fragShader = this.createShader(gl.FRAGMENT_SHADER, gizmoFragmentShader);
        
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertShader);
        gl.attachShader(this.program, fragShader);
        gl.linkProgram(this.program);
        
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            throw new Error('Gizmo shader link error: ' + gl.getProgramInfoLog(this.program));
        }
        
        // Get locations
        this.locations = {
            position: gl.getAttribLocation(this.program, 'position'),
            color: gl.getAttribLocation(this.program, 'color'),
            projection: gl.getUniformLocation(this.program, 'projection'),
            view: gl.getUniformLocation(this.program, 'view'),
            model: gl.getUniformLocation(this.program, 'model'),
            gizmoPosition: gl.getUniformLocation(this.program, 'gizmoPosition'),
            scale: gl.getUniformLocation(this.program, 'scale')
        };
        
        // Create VAO for translation gizmo (arrows)
        this.createTranslationGizmo();
    }
    
    createShader(type, source) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.error('Gizmo shader compile error:', gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return null;
        }
        
        return shader;
    }
    
    createTranslationGizmo() {
        const gl = this.gl;
        
        // Arrow geometry for each axis
        const arrowLength = 1.0;
        const arrowHeadSize = 0.2;
        const shaftWidth = 0.02;
        
        // Define vertices for X, Y, Z arrows
        const vertices = [];
        const colors = [];
        
        // X axis (red)
        this.addArrow(vertices, colors, [1, 0, 0], [1, 0, 0], arrowLength, arrowHeadSize, shaftWidth);
        
        // Y axis (green)
        this.addArrow(vertices, colors, [0, 1, 0], [0, 1, 0], arrowLength, arrowHeadSize, shaftWidth);
        
        // Z axis (blue)
        this.addArrow(vertices, colors, [0, 0, 1], [0, 0, 1], arrowLength, arrowHeadSize, shaftWidth);
        
        // Create VAO
        this.vao = gl.createVertexArray();
        gl.bindVertexArray(this.vao);
        
        // Position buffer
        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(this.locations.position);
        gl.vertexAttribPointer(this.locations.position, 3, gl.FLOAT, false, 0, 0);
        
        // Color buffer
        const colorBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(this.locations.color);
        gl.vertexAttribPointer(this.locations.color, 3, gl.FLOAT, false, 0, 0);
        
        gl.bindVertexArray(null);
        
        this.vertexCount = vertices.length / 3;
    }
    
    addArrow(vertices, colors, direction, color, length, headSize, shaftWidth) {
        const [dx, dy, dz] = direction;
        
        // Create shaft (line)
        vertices.push(0, 0, 0);
        colors.push(...color);
        vertices.push(dx * length, dy * length, dz * length);
        colors.push(...color);
        
        // Create arrowhead (cone)
        const headBase = length - headSize;
        const numSegments = 8;
        
        for (let i = 0; i < numSegments; i++) {
            const angle1 = (i / numSegments) * Math.PI * 2;
            const angle2 = ((i + 1) / numSegments) * Math.PI * 2;
            
            // Calculate perpendicular vectors
            let perp1, perp2;
            if (Math.abs(dx) < 0.9) {
                perp1 = [1, 0, 0];
            } else {
                perp1 = [0, 1, 0];
            }
            
            // Cross product to get perpendicular
            const cross = [
                dy * perp1[2] - dz * perp1[1],
                dz * perp1[0] - dx * perp1[2],
                dx * perp1[1] - dy * perp1[0]
            ];
            
            // Normalize
            const len = Math.sqrt(cross[0]**2 + cross[1]**2 + cross[2]**2);
            perp1 = [cross[0]/len, cross[1]/len, cross[2]/len];
            
            // Second perpendicular
            perp2 = [
                dy * perp1[2] - dz * perp1[1],
                dz * perp1[0] - dx * perp1[2],
                dx * perp1[1] - dy * perp1[0]
            ];
            
            // Triangle for cone segment
            vertices.push(dx * length, dy * length, dz * length); // Tip
            colors.push(...color);
            
            const x1 = dx * headBase + (Math.cos(angle1) * perp1[0] + Math.sin(angle1) * perp2[0]) * headSize;
            const y1 = dy * headBase + (Math.cos(angle1) * perp1[1] + Math.sin(angle1) * perp2[1]) * headSize;
            const z1 = dz * headBase + (Math.cos(angle1) * perp1[2] + Math.sin(angle1) * perp2[2]) * headSize;
            vertices.push(x1, y1, z1);
            colors.push(...color);
            
            const x2 = dx * headBase + (Math.cos(angle2) * perp1[0] + Math.sin(angle2) * perp2[0]) * headSize;
            const y2 = dy * headBase + (Math.cos(angle2) * perp1[1] + Math.sin(angle2) * perp2[1]) * headSize;
            const z2 = dz * headBase + (Math.cos(angle2) * perp1[2] + Math.sin(angle2) * perp2[2]) * headSize;
            vertices.push(x2, y2, z2);
            colors.push(...color);
        }
    }
    
    setMode(mode) {
        this.mode = mode;
        // TODO: Recreate geometry based on mode
    }
    
    setPosition(position) {
        this.position = [...position];
    }
    
    setScale(scale) {
        this.gizmoScale = scale;
    }
    
    render(projMatrix, viewMatrix, modelMatrix) {
        const gl = this.gl;
        
        // Save current GL state
        const depthTest = gl.isEnabled(gl.DEPTH_TEST);
        
        // Configure for gizmo rendering
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.ALWAYS); // Always render on top
        gl.lineWidth(3); // Thicker lines for visibility
        
        // Use gizmo program
        gl.useProgram(this.program);
        
        // Set uniforms
        gl.uniformMatrix4fv(this.locations.projection, false, projMatrix);
        gl.uniformMatrix4fv(this.locations.view, false, viewMatrix);
        gl.uniformMatrix4fv(this.locations.model, false, modelMatrix);
        gl.uniform3fv(this.locations.gizmoPosition, this.position);
        gl.uniform1f(this.locations.scale, this.gizmoScale);
        
        // Render gizmo
        gl.bindVertexArray(this.vao);
        
        // Draw lines for shafts
        gl.drawArrays(gl.LINES, 0, 6); // 3 axes * 2 vertices each
        
        // Draw triangles for arrowheads
        gl.drawArrays(gl.TRIANGLES, 6, this.vertexCount - 6);
        
        gl.bindVertexArray(null);
        
        // Restore GL state
        if (depthTest) {
            gl.enable(gl.DEPTH_TEST);
        } else {
            gl.disable(gl.DEPTH_TEST);
        }
        gl.depthFunc(gl.LESS);
    }
    
    // Ray-axis intersection for interaction
    intersectRay(rayOrigin, rayDirection, viewMatrix, projMatrix, viewport) {
        // Transform gizmo position to screen space
        const worldPos = [...this.position, 1];
        
        // Apply view and projection
        const viewPos = this.transformPoint(worldPos, viewMatrix);
        const clipPos = this.transformPoint(viewPos, projMatrix);
        
        // Perspective divide
        const ndcPos = [
            clipPos[0] / clipPos[3],
            clipPos[1] / clipPos[3],
            clipPos[2] / clipPos[3]
        ];
        
        // To screen space
        const screenPos = [
            (ndcPos[0] + 1) * viewport[2] / 2,
            (1 - ndcPos[1]) * viewport[3] / 2
        ];
        
        // Check each axis
        const axes = [
            { axis: 'x', direction: [1, 0, 0], color: [1, 0, 0] },
            { axis: 'y', direction: [0, 1, 0], color: [0, 1, 0] },
            { axis: 'z', direction: [0, 0, 1], color: [0, 0, 1] }
        ];
        
        let closestAxis = null;
        let closestDistance = Infinity;
        
        for (const { axis, direction } of axes) {
            // Project axis to screen
            const axisEnd = [
                this.position[0] + direction[0] * this.gizmoScale,
                this.position[1] + direction[1] * this.gizmoScale,
                this.position[2] + direction[2] * this.gizmoScale,
                1
            ];
            
            const viewEnd = this.transformPoint(axisEnd, viewMatrix);
            const clipEnd = this.transformPoint(viewEnd, projMatrix);
            const ndcEnd = [
                clipEnd[0] / clipEnd[3],
                clipEnd[1] / clipEnd[3],
                clipEnd[2] / clipEnd[3]
            ];
            const screenEnd = [
                (ndcEnd[0] + 1) * viewport[2] / 2,
                (1 - ndcEnd[1]) * viewport[3] / 2
            ];
            
            // Distance from ray to line segment
            const distance = this.pointToLineDistance(
                [rayOrigin[0], rayOrigin[1]],
                screenPos,
                screenEnd
            );
            
            if (distance < 20 && distance < closestDistance) { // 20 pixel threshold
                closestDistance = distance;
                closestAxis = axis;
            }
        }
        
        return closestAxis;
    }
    
    transformPoint(point, matrix) {
        const result = [0, 0, 0, 0];
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                result[i] += matrix[i * 4 + j] * point[j];
            }
        }
        return result;
    }
    
    pointToLineDistance(point, lineStart, lineEnd) {
        const A = point[0] - lineStart[0];
        const B = point[1] - lineStart[1];
        const C = lineEnd[0] - lineStart[0];
        const D = lineEnd[1] - lineStart[1];
        
        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;
        
        if (lenSq !== 0) {
            param = dot / lenSq;
        }
        
        let xx, yy;
        
        if (param < 0) {
            xx = lineStart[0];
            yy = lineStart[1];
        } else if (param > 1) {
            xx = lineEnd[0];
            yy = lineEnd[1];
        } else {
            xx = lineStart[0] + param * C;
            yy = lineStart[1] + param * D;
        }
        
        const dx = point[0] - xx;
        const dy = point[1] - yy;
        
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    dispose() {
        const gl = this.gl;
        if (this.vao) gl.deleteVertexArray(this.vao);
        if (this.program) gl.deleteProgram(this.program);
    }
}